<template>
  <transition>
    <div v-if="show" class="modal-mask">
      <div class="modal-wrapper">
        <div class="modal-container saved-search-modal">
          <div class="header-lockup">
            <div class="text">
              <h4>
                {{ searchLabel }}
              </h4>
            </div>
            <button class="Close" @click="closeModal">
              <span>Close</span>
            </button>
          </div>
          <div class="scroller">
            <FormKit
              v-model="formData"
              type="form"
              :actions="false"
              :form-errors="formErrors"
              name="savedSearch"
              @submit="editSearch ? updateFilters() : saveFilters()"
            >
              <div>
                <div class="form-block">
                  <FormKit
                    name="name"
                    label="Search Name"
                    validation="required:trim"
                    @keydown.enter.prevent=""
                    @keyup.enter.prevent="
                      editSearch ? updateFilters() : saveFilters()
                    "
                  />
                </div>
                <div class="form-block mb-reduced">
                  <div
                    class="formulate-input-label formulate-input-label--before"
                  >
                    Filters in This Search
                    <div role="region" class="appliedfilters">
                      <SelectedFilterChips
                        :fix-expanded="true"
                        :hide-save="true"
                        :hide-clear="true"
                        :selected-filters="editSearch ? editChips : localChips"
                        :clear-filters-url="clearFiltersUrl"
                        @filterToggled="toggleFilter"
                      />
                    </div>
                  </div>
                </div>
                <div
                  v-if="
                    compareCurrentSearch &&
                    editSearch &&
                    savedSearchQueryString(editSelectedFilters) !==
                      savedSearchQueryString(selectedFilters)
                  "
                  class="form-block"
                >
                  <div
                    class="formulate-input-label formulate-input-label--before"
                  >
                    Filters in Your Current Shopping Page
                  </div>
                  <p>
                    You can update this saved search to use the filters you
                    currently have applied to your shopping page.
                  </p>
                  <div role="region" class="appliedfilters currentlyapplied">
                    <SelectedFilterChips
                      :fix-expanded="true"
                      :hide-save="true"
                      :hide-clear="true"
                      :selected-filters="localChips"
                      :clear-filters-url="clearFiltersUrl"
                      @filterToggled="toggleFilter"
                    />
                  </div>
                  <div class="input-checkbox">
                    <input
                      id="update-search"
                      v-model="clone_current"
                      type="checkbox"
                    />
                    <label for="update-search"
                      >Update this Saved Search to match current</label
                    >
                  </div>
                </div>
                <div class="form-block mb-0">
                  <div
                    class="formulate-input-label formulate-input-label--before"
                  >
                    Notification Type
                  </div>
                  <p class="text-left">
                    We'll notify you when matching wines arrive.
                  </p>
                  <ul class="checkboxes inline-checkboxes">
                    <li>
                      <div class="input-checkbox">
                        <FormKit
                          name="notify_app"
                          type="checkbox"
                          label="App"
                        />
                      </div>
                    </li>
                    <li>
                      <div class="input-checkbox">
                        <FormKit
                          name="notify_email"
                          type="checkbox"
                          label="Email"
                        />
                      </div>
                    </li>
                    <li>
                      <div class="input-checkbox">
                        <FormKit
                          name="notify_text"
                          type="checkbox"
                          label="Text"
                        />
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div v-if="editSearch" class="button-options">
                <FormKit
                  type="submit"
                  :disabled="saving"
                  label="Update"
                  :classes="{
                    outer: { $reset: true },
                    inner: {
                      $reset: true,
                    },
                    wrapper: { $reset: true },
                  }"
                />
                <FormKit
                  type="button"
                  :disabled="saving"
                  input-class="button-outline"
                  label="Delete Saved Search"
                  :classes="{
                    outer: { $reset: true },
                    inner: {
                      $reset: true,
                    },
                    wrapper: { $reset: true },
                  }"
                  @click="deleteFilter"
                />
              </div>
              <div v-else class="button-options">
                <FormKit
                  type="submit"
                  :disabled="saving"
                  label="Save"
                  :classes="{
                    outer: { $reset: true },
                    inner: {
                      $reset: true,
                    },
                    wrapper: { $reset: true },
                    messages: { $reset: true },
                  }"
                />
              </div>
            </FormKit>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
<script>
import { getParamsFromFilters } from '#wa/composables/useProductResults'
import { defaultUrlToFilterParser } from '#wa/utils/filters/default'
import {
  condenseRegionsToAttribute,
  getChildSlugs,
} from '#wa/utils/filters/regionSlugs'
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'

export default {
  name: 'ProductSavedSearch',
  components: {
    SelectedFilterChips: defineAsyncComponent(
      () => import('@/components/SelectedFilterChips'),
    ),
  },
  props: {
    selectedFilters: {
      type: Array,
      required: false,
      default: () => {
        return []
      },
    },
    clearFiltersUrl: {
      type: String,
      default: '',
    },
    show: {
      type: Boolean,
      default: false,
    },
    editSearch: {
      type: Number,
      required: false,
      default: undefined,
    },
    compareCurrentSearch: {
      type: Boolean,
      default: true,
    },
    useAllRegions: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'saveComplete'],
  data() {
    return {
      localSelectedFilters: [],
      editSelectedFilters: [],
      clone_current: false,
      formErrors: [],
      inputErrors: {},
      formData: {
        name: '',
        notify_text: false,
        notify_app: false,
        notify_email: false,
      },
      saving: false,
    }
  },
  computed: {
    ...mapGetters(['allRegionsIndex']),
    searchLabel() {
      if (this.editSearch) {
        return 'Edit Saved Search'
      }
      return 'Save Search'
    },
    ordering() {
      return this.productResults.orderingFromUrl(
        this.$route,
        this.selectedFilters,
      )
    },
    editChips() {
      return this.displayChips(this.editSelectedFilters)
    },
    localChips() {
      return this.displayChips(this.localSelectedFilters)
    },
  },
  watch: {
    show(newValue, oldValue) {
      if (newValue) {
        if (this.editSearch) {
          this.$axios
            .get(`api/search/user-saved-search/${this.editSearch}`)
            .then((resp) => {
              const query = resp.data.query_string
              const searchParams = new URLSearchParams(query)
              this.editSelectedFilters = defaultUrlToFilterParser({
                axios: this.$axios,
                params: {},
                query: Object.fromEntries(searchParams.entries()),
              })
              this.formData = {
                name: resp.data.name,
                notify_text: resp.data.send_sms,
                notify_app: resp.data.send_push,
                notify_email: resp.data.send_email,
              }
            })
        } else {
          this.formData = {
            name: '',
            notify_text: false,
            notify_app: false,
            notify_email: false,
          }
        }
        this.formErrors = []
        this.inputErrors = {}
        this.clone_current = false
        // At the point of the modal being opened we need
        // to create a local copy of all existing filters
        // for our own possible modification.
        this.localSelectedFilters = cloneDeep(this.selectedFilters)
      }
    },
  },
  created() {
    this.metadata = useMetadata()
    this.productResults = useProductResults()
  },
  methods: {
    objToQueryString(obj) {
      const params = new URLSearchParams(obj)
      return params.toString()
    },
    async saveFilters() {
      if (this.saving) return
      this.saving = true
      const queryString = this.savedSearchQueryString(this.localSelectedFilters)
      try {
        await this.$axios.post('api/search/user-saved-search/', {
          name: this.formData.name,
          send_email: this.formData.notify_email,
          send_sms: this.formData.notify_text,
          send_push: this.formData.notify_app,
          notification_preference: 'daily',
          original_query_string: queryString,
        })
        this.formErrors = []
        this.inputErrors = {}
      } catch (error) {
        if (error.response && error.response.data) {
          if (error.response.data.non_field_errors) {
            this.formErrors = [error.response.data.non_field_errors[0]]
            console.error(error.response.data.non_field_errors[0])
          } else if (error.response.status === 400) {
            this.formErrors = ['Error: Unable to save this search.']
          } else {
            this.inputErrors = error.response.data
          }
        } else {
          throw error
        }
        this.saving = false
        return
      }
      await this.$store.dispatch('getSavedSearches')
      this.$emit('close')
      await this.$store.dispatch('toggleSavedSearchModal')
      this.$emit('saveComplete')
      this.saving = false
    },
    async updateFilters() {
      if (this.saving) return
      this.saving = true

      const selectedFilters = this.clone_current
        ? this.localSelectedFilters
        : this.editSelectedFilters
      const queryString = this.savedSearchQueryString(selectedFilters)

      try {
        await this.$axios.put(
          `api/search/user-saved-search/${this.editSearch}/`,
          {
            name: this.formData.name,
            send_email: this.formData.notify_email,
            send_sms: this.formData.notify_text,
            send_push: this.formData.notify_app,
            notification_preference: 'daily',
            original_query_string: queryString,
          },
        )
        this.formErrors = []
        this.inputErrors = {}
      } catch (error) {
        if (error.response && error.response.data) {
          if (error.response.data.non_field_errors) {
            this.formErrors = [error.response.data.non_field_errors[0]]
            console.error(error.response.data.non_field_errors[0])
          } else if (error.response.status === 400) {
            this.formErrors = ['Error: Search term too long.']
          } else {
            this.inputErrors = error.response.data
          }
        } else {
          this.saving = false
          throw error
        }
        this.saving = false
        return
      }

      await this.$store.dispatch('getSavedSearches')
      this.$emit('close')
      await this.$store.dispatch('toggleSavedSearchModal')
      this.saving = false
    },
    savedSearchQueryString(filters) {
      const params = getParamsFromFilters(filters, null, null, this.ordering)
      return Object.keys(params)
        .sort()
        .filter((key) => key !== 'show_sold_out')
        .map((key) => key + '=' + params[key])
        .join('&')
    },
    async deleteFilter() {
      this.saving = true
      await this.$axios.delete(
        `api/search/user-saved-search/${this.editSearch}/`,
      )
      this.$store.dispatch('getSavedSearches')
      this.$emit('close')
      this.$store.dispatch('toggleSavedSearchModal')
      this.saving = false
    },
    toggleFilter(e) {
      const updateFilterArray = (filterArray) => {
        let tempArray = cloneDeep(filterArray)
        const filterIndex = tempArray.findIndex(
          (f) => f.category === e.category && f.key === e.key,
        )
        if (e.category === 'region_slug') {
          const region = (
            this.useAllRegions
              ? this.allRegionsIndex
              : this.metadata.regionsIndex
          ).find((r) => r.slug === e.slug)
          const deselectSlugs = getChildSlugs(region)
          tempArray = tempArray.filter((s) => !deselectSlugs.includes(s.key))
        }
        if (filterIndex >= 0) {
          tempArray.splice(filterIndex, 1)
        } else {
          tempArray.push(e)
        }
        return tempArray
      }
      if (this.editSearch) {
        this.editSelectedFilters = updateFilterArray(this.editSelectedFilters)
      } else {
        this.localSelectedFilters = updateFilterArray(this.localSelectedFilters)
      }
    },
    displayChips(filters) {
      const localSelectedRegions = condenseRegionsToAttribute(
        this.useAllRegions
          ? this.$store.getters.allRegionsFilters
          : this.metadata.regionsFilters,
        filters.filter((f) => f.category === 'region_slug').map((f) => f.key),
        'slug',
      )
      const condensedRegions = localSelectedRegions.map((slug) => {
        const index = (
          this.useAllRegions ? this.allRegionsIndex : this.metadata.regionsIndex
        ).find((r) => r.slug === slug)
        return {
          chipName: index.chipName,
          name: index.name,
          key: index.slug,
          slug: index.slug,
          category: 'region_slug',
        }
      })
      return filters
        .filter((f) => f.category !== 'region_slug')
        .concat(condensedRegions)
    },
    closeModal() {
      this.$emit('close')
      this.$store.dispatch('toggleSavedSearchModal')
    },
  },
}
</script>
